import { findShortcode } from './findShortcode';
import { ShortcodeMatch } from '../type';

/**
 * Applies a transformation function to each shortcode found in the text.
 *
 * This function searches for shortcodes with the specified name in the provided text,
 * and applies a transformation function to each found shortcode. The transformation
 * function is used to generate a replacement string for each shortcode.
 *
 * @param text - The text containing shortcodes to be transformed.
 * @param name - The name of the shortcode to find and transform (e.g., "price").
 * @param func - A transformation function that takes a ShortcodeMatch object and returns
 *               a string to replace the shortcode. Defaults to a function that logs the match
 *               and returns 'test'.
 * @returns The modified text with all instances of the specified shortcode replaced by
 *          the result of the transformation function.
 *
 * @example
 * const text = "The total is [price size='large']$10[/price].";
 * const result = applyShortcode(text, 'price', (match) => {
 *   return `<span class="price">${match.content}</span>`;
 * });
 * // result will be "The total is <span class="price">$10</span>."
 */
export const applyShortcode = (
  text: string,
  name: string,
  func: (match: ShortcodeMatch) => string,
): string => {
  const matches = findShortcode(text, name);

  if (!matches) {
    return text;
  }

  let modifiedText = text;

  matches.forEach((match) => {
    const replacement = func(match);
    modifiedText = modifiedText.replace(match.raw, replacement);
  });

  return modifiedText;
};
